import { useState } from "react";
import { api } from "@/lib/api";
import { Link } from "@tanstack/react-router";
import { SEO } from "@/components/SEO";
import { SPFResult } from "@/components/tool-results";

export function SPFCheckerPage() {
  const [domain, setDomain] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);

  const runCheck = async () => {
    if (!domain) return;
    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const data = await api.tools.validateSPF(domain);
      setResult(data);
    } catch (err: any) {
      setError(err.message || "An error occurred");
    } finally {
      setLoading(false);
    }
  };

  const schemaData = {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    "name": "Free SPF Record Checker",
    "description": "Check and validate your SPF record instantly. Identify DNS lookup limits, syntax errors, and misconfigurations. No signup required.",
    "url": "https://getsent.dev/tools/spf-checker",
    "applicationCategory": "SecurityApplication",
    "operatingSystem": "Any",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "USD"
    }
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "What is an SPF record?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "SPF (Sender Policy Framework) is a DNS TXT record that specifies which mail servers are authorized to send email on behalf of your domain. It helps prevent email spoofing by allowing receiving servers to verify that incoming mail from a domain comes from an authorized IP address."
        }
      },
      {
        "@type": "Question",
        "name": "How many DNS lookups can an SPF record have?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "SPF records are limited to 10 DNS lookups. This includes mechanisms like 'include:', 'a:', 'mx:', 'ptr:', and 'redirect='. Exceeding this limit causes SPF validation to fail with a 'permerror', which can result in emails being rejected."
        }
      },
      {
        "@type": "Question",
        "name": "What does SPF softfail (~all) vs hardfail (-all) mean?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "The '-all' (hardfail) tells receivers to reject emails from unauthorized servers. The '~all' (softfail) suggests marking unauthorized emails as suspicious but not rejecting them. For best security, use '-all' once you've verified all legitimate sending sources are included."
        }
      }
    ]
  };

  return (
    <div>
      <SEO
        title="Free SPF Record Checker - Validate Your SPF DNS Record"
        description="Check your SPF record for errors, DNS lookup limits, and misconfigurations. Free SPF validator with instant results. No signup required."
        canonical="/tools/spf-checker"
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />

      {/* Hero */}
      <section className="relative overflow-hidden border-b bg-gradient-to-b from-indigo-50/50 to-transparent py-16 dark:from-indigo-950/20 sm:py-24">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-2xl text-center">
            <div className="mb-4 inline-flex items-center gap-2 rounded-full bg-indigo-500/10 px-4 py-1.5 text-sm font-medium text-indigo-600">
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" />
              </svg>
              Free Tool
            </div>
            <h1 className="text-4xl font-bold tracking-tight sm:text-5xl">
              SPF Record{" "}
              <span className="bg-gradient-to-r from-indigo-500 to-purple-600 bg-clip-text text-transparent">
                Checker
              </span>
            </h1>
            <p className="mt-4 text-lg text-muted-foreground">
              Validate your SPF record and check for common issues like DNS lookup limits,
              syntax errors, and missing authorized senders.
            </p>
          </div>
        </div>
      </section>

      {/* Tool */}
      <section className="py-12 sm:py-16">
        <div className="mx-auto max-w-3xl px-6 lg:px-8">
          <div className="rounded-2xl border bg-card p-6 shadow-lg">
            <div className="flex flex-col gap-4 sm:flex-row">
              <div className="flex-1">
                <label className="mb-2 block text-sm font-medium">Enter your domain</label>
                <input
                  type="text"
                  value={domain}
                  onChange={(e) => setDomain(e.target.value)}
                  placeholder="example.com"
                  className="w-full rounded-lg border bg-background px-4 py-3 text-base focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
                  onKeyDown={(e) => e.key === "Enter" && runCheck()}
                />
              </div>
              <div className="flex items-end">
                <button
                  onClick={runCheck}
                  disabled={loading || !domain}
                  className="w-full rounded-lg bg-gradient-to-r from-indigo-500 to-purple-600 px-8 py-3 font-semibold text-white shadow-lg shadow-indigo-500/25 transition-all hover:shadow-xl hover:shadow-indigo-500/30 disabled:opacity-50 sm:w-auto"
                >
                  {loading ? "Checking..." : "Check SPF"}
                </button>
              </div>
            </div>
          </div>

          {error && (
            <div className="mt-6 rounded-lg border border-red-500/20 bg-red-500/10 p-4 text-red-600">
              {error}
            </div>
          )}

          {result && (
            <div className="mt-6">
              <SPFResult data={result} />
            </div>
          )}
        </div>
      </section>

      {/* What is SPF */}
      <section className="border-t py-12 sm:py-16">
        <div className="mx-auto max-w-3xl px-6 lg:px-8">
          <h2 className="text-2xl font-bold">What is an SPF Record?</h2>
          <div className="mt-4 space-y-4 text-muted-foreground">
            <p>
              <strong className="text-foreground">SPF (Sender Policy Framework)</strong> is a DNS TXT record that
              specifies which mail servers are authorized to send email on behalf of your domain. When an email
              is received, the recipient's mail server checks the SPF record to verify the sender is legitimate.
            </p>
            <p>
              An SPF record looks like this:
            </p>
            <code className="block rounded-lg bg-muted p-4 text-sm text-foreground">
              v=spf1 include:_spf.google.com include:amazonses.com -all
            </code>
            <p>
              This record authorizes Google Workspace and Amazon SES to send emails for the domain,
              and rejects (<code>-all</code>) all other sources.
            </p>
          </div>
        </div>
      </section>

      {/* Common Issues */}
      <section className="border-t py-12 sm:py-16">
        <div className="mx-auto max-w-3xl px-6 lg:px-8">
          <h2 className="text-2xl font-bold">Common SPF Issues We Check</h2>
          <div className="mt-6 grid gap-4">
            <div className="rounded-xl border bg-card p-5">
              <h3 className="font-semibold text-red-600">DNS Lookup Limit Exceeded</h3>
              <p className="mt-1 text-sm text-muted-foreground">
                SPF records are limited to 10 DNS lookups. Each <code>include:</code>, <code>a:</code>,
                and <code>mx:</code> mechanism counts toward this limit. Exceeding it causes SPF to fail.
              </p>
            </div>
            <div className="rounded-xl border bg-card p-5">
              <h3 className="font-semibold text-amber-600">Multiple SPF Records</h3>
              <p className="mt-1 text-sm text-muted-foreground">
                A domain should have only one SPF record. Multiple records cause validation failures.
                Combine all authorized senders into a single record.
              </p>
            </div>
            <div className="rounded-xl border bg-card p-5">
              <h3 className="font-semibold text-amber-600">Using +all or ?all</h3>
              <p className="mt-1 text-sm text-muted-foreground">
                The <code>+all</code> mechanism allows anyone to send as your domain, defeating the
                purpose of SPF. Use <code>-all</code> (reject) or <code>~all</code> (softfail) instead.
              </p>
            </div>
            <div className="rounded-xl border bg-card p-5">
              <h3 className="font-semibold text-amber-600">Missing Sending Services</h3>
              <p className="mt-1 text-sm text-muted-foreground">
                If you use email services like Mailchimp, HubSpot, or Salesforce, their sending
                servers must be included in your SPF record, or their emails will fail authentication.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="border-t py-12 sm:py-16">
        <div className="mx-auto max-w-3xl px-6 lg:px-8">
          <h2 className="text-2xl font-bold">Frequently Asked Questions</h2>
          <div className="mt-6 space-y-6">
            <div>
              <h3 className="font-semibold">What is an SPF record?</h3>
              <p className="mt-2 text-muted-foreground">
                SPF (Sender Policy Framework) is a DNS TXT record that specifies which mail servers
                are authorized to send email on behalf of your domain. It helps prevent email spoofing
                by allowing receiving servers to verify that incoming mail from a domain comes from
                an authorized IP address.
              </p>
            </div>
            <div>
              <h3 className="font-semibold">How many DNS lookups can an SPF record have?</h3>
              <p className="mt-2 text-muted-foreground">
                SPF records are limited to 10 DNS lookups. This includes mechanisms like <code>include:</code>,
                <code>a:</code>, <code>mx:</code>, <code>ptr:</code>, and <code>redirect=</code>.
                Exceeding this limit causes SPF validation to fail with a "permerror", which can
                result in emails being rejected.
              </p>
            </div>
            <div>
              <h3 className="font-semibold">What does SPF softfail (~all) vs hardfail (-all) mean?</h3>
              <p className="mt-2 text-muted-foreground">
                The <code>-all</code> (hardfail) tells receivers to reject emails from unauthorized
                servers. The <code>~all</code> (softfail) suggests marking unauthorized emails as
                suspicious but not rejecting them. For best security, use <code>-all</code> once
                you've verified all legitimate sending sources are included.
              </p>
            </div>
            <div>
              <h3 className="font-semibold">How do I fix "too many DNS lookups"?</h3>
              <p className="mt-2 text-muted-foreground">
                You can reduce DNS lookups by: (1) using <code>ip4:</code> and <code>ip6:</code>
                mechanisms instead of <code>include:</code> where possible, (2) removing unused
                services, (3) using SPF flattening tools that resolve includes to IP addresses,
                or (4) consolidating services that share infrastructure.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Related Tools */}
      <section className="border-t py-12 sm:py-16">
        <div className="mx-auto max-w-3xl px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-center">Related Tools</h2>
          <div className="mt-6 grid gap-4 sm:grid-cols-2">
            <Link
              to="/tools/dkim-checker"
              className="group rounded-xl border bg-card p-5 transition-all hover:border-indigo-500/50 hover:shadow-lg"
            >
              <h3 className="font-semibold group-hover:text-indigo-600">DKIM Checker</h3>
              <p className="mt-1 text-sm text-muted-foreground">
                Validate your DKIM records and discover configured selectors.
              </p>
            </Link>
            <Link
              to="/tools/dmarc-checker"
              className="group rounded-xl border bg-card p-5 transition-all hover:border-indigo-500/50 hover:shadow-lg"
            >
              <h3 className="font-semibold group-hover:text-indigo-600">DMARC Checker</h3>
              <p className="mt-1 text-sm text-muted-foreground">
                Check your DMARC policy and reporting configuration.
              </p>
            </Link>
            <Link
              to="/tools/email-authentication-checker"
              className="group rounded-xl border bg-card p-5 transition-all hover:border-indigo-500/50 hover:shadow-lg"
            >
              <h3 className="font-semibold group-hover:text-indigo-600">Full Email Check</h3>
              <p className="mt-1 text-sm text-muted-foreground">
                Check SPF, DKIM, and DMARC all at once.
              </p>
            </Link>
            <Link
              to="/blog/$slug"
              params={{ slug: "spf-flattening-dns-lookup-limit" }}
              className="group rounded-xl border bg-card p-5 transition-all hover:border-indigo-500/50 hover:shadow-lg"
            >
              <h3 className="font-semibold group-hover:text-indigo-600">SPF Flattening Guide</h3>
              <p className="mt-1 text-sm text-muted-foreground">
                Learn how to solve the 10 DNS lookup limit problem.
              </p>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="border-t py-16">
        <div className="mx-auto max-w-7xl px-6 text-center lg:px-8">
          <h2 className="text-2xl font-bold sm:text-3xl">
            Want continuous SPF monitoring?
          </h2>
          <p className="mt-2 text-muted-foreground">
            Get alerts when your SPF record changes or breaks. Free for one domain.
          </p>
          <Link
            to="/register"
            className="mt-6 inline-flex rounded-full bg-gradient-to-r from-indigo-500 to-purple-600 px-8 py-3 font-semibold text-white shadow-lg shadow-indigo-500/25 transition-all hover:shadow-xl hover:shadow-indigo-500/30"
          >
            Start free monitoring
          </Link>
        </div>
      </section>
    </div>
  );
}
